import { apiClient } from "./client";
import {
  ApiResponse,
  Project,
  BackendProject,
  SpecUploadResult,
} from "../types";

export interface ProjectListResponse {
  projects: Project[];
}

// Backend actually returns projects directly in the response
export interface BackendProjectListResponse {
  success: boolean;
  projects: BackendProject[];
}

export interface ProjectResponse {
  project: Project;
}

// Helper function to convert backend project to frontend project
function convertBackendProject(backendProject: BackendProject): Project {
  return {
    id: backendProject.project_id,
    name: backendProject.project_name,
    description: backendProject.description,
    workspace_id: backendProject.workspace_id,
  };
}

export class ProjectAPI {
  static async getAllProjects(): Promise<ApiResponse<ProjectListResponse>> {
    try {
      const response = await apiClient.get<any>("/project/get_all");
      console.log("🔍 Raw /project/get_all response:", response);

      if (response.success && response.data) {
        let projects: BackendProject[] = [];

        // Handle double-nested response structure
        if (response.data.success && response.data.projects) {
          // Backend response wrapped by apiClient
          projects = response.data.projects;
        } else if (Array.isArray(response.data.projects)) {
          // Direct projects array
          projects = response.data.projects;
        } else if (Array.isArray(response.data)) {
          // Direct array response
          projects = response.data;
        }

        console.log("📂 Raw projects from API:", projects);

        // Convert backend projects to frontend format
        const convertedProjects = projects.map(convertBackendProject);
        console.log("🔄 Converted projects:", convertedProjects);

        return {
          success: true,
          data: {
            projects: convertedProjects,
          },
        };
      } else {
        return {
          success: false,
          error: response.error || "Failed to load projects",
        };
      }
    } catch (error) {
      console.error("❌ Project API error:", error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Failed to load projects",
      };
    }
  }

  static async getProject(
    projectId: string
  ): Promise<ApiResponse<ProjectResponse>> {
    const formData = new FormData();
    formData.append("project_id", projectId);

    return apiClient.postForm<ProjectResponse>(
      "/project/get_project",
      formData
    );
  }

  static async getAssignees(projectId: string): Promise<
    ApiResponse<{
      users: Array<{
        project_id: string;
        user: {
          email: string;
          icon_file_id: string;
          icon_uri: string;
          id: string;
          joined_at: string;
          name: string;
          project_workspace_user_status_id: string;
          sqripts_id: string;
          workspace_id: string;
          workspace_user_status_id: string;
        };
      }>;
    }>
  > {
    return apiClient.get(`/project/${encodeURIComponent(projectId)}/assignees`);
  }

  static async uploadProjectRules(
    projectId: string,
    file: File
  ): Promise<ApiResponse<void>> {
    const formData = new FormData();
    formData.append("project_id", projectId);
    formData.append("file", file);
    formData.append("creation_time", new Date(file.lastModified).toISOString());

    return apiClient.postForm<void>("/document/upload_project_rules", formData);
  }

  static async uploadSpecificationDocs(
    projectId: string,
    file: File,
    uploaderName?: string
  ): Promise<ApiResponse<SpecUploadResult>> {
    const formData = new FormData();
    formData.append("project_id", projectId);
    formData.append("file", file);
    formData.append(
      "uploader_name",
      uploaderName ?? "unknown"
    );
    formData.append(
      "browser_timezone",
      Intl.DateTimeFormat().resolvedOptions().timeZone
    );

    // 取り込みの内訳（取り込めなかったファイル・要約を作れなかったファイル）を
    // 画面で伝えるため、応答ボディを型付きで受け取る（#164）
    return apiClient.postForm<SpecUploadResult>(
      "/document/upload_spec_docs",
      formData
    );
  }

  static async deleteSpecificationDocs(
    projectId: string
  ): Promise<ApiResponse<void>> {
    const formData = new FormData();
    formData.append("project_id", projectId);

    return apiClient.postForm<void>("/document/delete_all_spec_docs", formData);
  }

  /** 指定したZIP（source_zip_name）に属する仕様書のみ削除（複数ZIP対応・個別削除） */
  static async deleteSpecificationDocsByZip(
    projectId: string,
    sourceZipName: string
  ): Promise<ApiResponse<void>> {
    return apiClient.post<void>("/document/delete_spec_docs_by_source_zip", {
      project_id: projectId,
      source_zip_name: sourceZipName,
    });
  }

  static async deleteProjectRules(
    projectId: string
  ): Promise<ApiResponse<void>> {
    const formData = new FormData();
    formData.append("project_id", projectId);
    return apiClient.postForm<void>("/document/delete_project_rules", formData);
  }

  static async getProjectRulesInfo(
    projectId: string
  ): Promise<ApiResponse<any>> {
    return apiClient.get<any>(
      `/document/get_project_rules_file_info?project_id=${projectId}`
    );
  }

  static async getSpecificationDocsInfo(
    projectId: string
  ): Promise<ApiResponse<any>> {
    return apiClient.get<any>(
      `/document/get_specification_files_info?project_id=${projectId}`
    );
  }
}

export default ProjectAPI;
